import React, { useEffect, useState, useContext } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FormContext } from "../context/FormContext";
import { es } from "date-fns/locale";
import { getHoras } from "./getData";
import "../styles/Calendario.css"; // Archivo CSS personalizado
import { formatFecha } from "../hooks/useFetchData";

const horariosPorPais = {
  ec: { AM: 9, PM: 10 },
  pe: { AM: 1, PM: 2 },
  co: { AM: 6, PM: 7 },
  cl: { AM: 4, PM: 5 }, // Valores actuales
};
// Registrar la configuración personalizada para que el domingo sea el primer día
registerLocale("es-custom", {
  ...es,
  options: {
    ...es.options,
    weekStartsOn: 0,
  },
});

const CalendarComponent = ({ onCalendarioSeleccionado }) => {
  const { formData, updateFormData } = useContext(FormContext);
  const [activeTab, setActiveTab] = useState("AM");
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(false);

  function getFirstAvailableDate() {
    let date = new Date();
    date.setDate(date.getDate()); // Comenzar desde mañana
    
    // Buscar siguiente día hábil
    while (date.getDay() === 0) {
      date.setDate(date.getDate());
    }
    
    return date;
  }

  // Datos del formulario desde el contexto para configurar el calendario
  // console.log("FORM DATA DESDE EL CALENDARIO",formData)

  const [selectedDate, setSelectedDate] = useState(() =>
    getFirstAvailableDate(new Date())
  );

  useEffect(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
  
    // Si es antes de las 14:30, AM. Si no, PM.
    if (hours < 14 || (hours === 14 && minutes < 30)) {
      setActiveTab("AM");
    } else {
      setActiveTab("PM");
    }
  
    // También actualizamos el selectedHorarios basado en el país y el turno actual
    const pais = formData.pais || "cl";
    const turno = (hours < 14 || (hours === 14 && minutes < 30)) ? "AM" : "PM";
    setSelectedHorarios(horariosPorPais[pais]?.[turno] || 4);
  }, []);
  


  useEffect(() => {
    if (formData.fechaSeleccionada) {
      const fechaGuardada = new Date(formData.fechaSeleccionada);
      if (!isNaN(fechaGuardada.getTime())) {
        setSelectedDate(fechaGuardada);
       
      }
    } else {
      updateFormData({ fechaSeleccionada: selectedDate });
    
    }
  }, [formData.fechaSeleccionada]);

  const [selectedHorarios, setSelectedHorarios] = useState(() => {
    const pais = formData.pais || "cl"; // Si no hay país, usar CL por defecto
    return horariosPorPais[pais]?.AM || 4; // Asignar AM del país o 4 por defecto
  });

  
  // Llamar al endpoint cada vez que cambie la fecha o el turno
  useEffect(() => {
    const fetchAvailableTimes = async () => {
      setLoading(true); // Activar el estado de carga
      try {
        const { TiendaSeleccionada, fechaSeleccionada } = formData;
        if (!TiendaSeleccionada || !fechaSeleccionada) {
          setAvailableTimes([]);
          return;
        }
  
        const selectedTienda = TiendaSeleccionada.cfg_codigo;
       
        const selectedServicio = formData.tpc_codigo;
  
        // Formatear la fecha
        const selectedDate = new Intl.DateTimeFormat("es-CL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
          .format(new Date(fechaSeleccionada))
          .replace(/-/g, "/");
        
   
        // Llamada al servicio para obtener horarios
        const horas = await getHoras(
          selectedTienda,
          selectedServicio,
          selectedHorarios,
          selectedDate
        );
        // Verificar y actualizar horarios disponibles
        if (horas && Array.isArray(horas)) {
          const fechaActual = new Date();
          const diaActual = fechaActual.toLocaleDateString('es-CL', {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          }).replace(/-/g, "/");

          const hora = fechaActual.getHours();
          const minutos = fechaActual.getMinutes();

          const horariosFiltrados = horas.filter((item) => {
            const [horaItem, minutoItem] = item.hora.split(":").map(Number);
            if(diaActual === selectedDate){
              return (horaItem > hora || (hora === horaItem && minutoItem >= minutos));
            }else{
              return true;
            }
          });
          setAvailableTimes(horariosFiltrados.map((item) => item.hora));
        } else {
          setAvailableTimes([]);
      
        }
      } catch (error) {
        // Error al obtener los horarios disponibles desde la API
        console.error("Error al obtener los horarios disponibles:", error);
        setAvailableTimes([]);
      } finally {
        setLoading(false); // Desactivar el estado de carga
      }
    };
  
    fetchAvailableTimes();
  }, [formData.fechaSeleccionada, activeTab]);
  

  const handleDateChange = (date) => {
    // Fecha seleccionada por el usuario en el calendario
    // console.log("Fecha seleccionada:", date);
    setSelectedDate(date);
    updateFormData({ fechaSeleccionada: date });
      // Mantener el tipo de horario seleccionado
  const pais = formData.pais || "cl";
  setSelectedHorarios(horariosPorPais[pais]?.[activeTab] || 4);
  };

  const handleTimeSelect = (time) => {
    // Hora seleccionada por el usuario
    // console.log("Hora seleccionada:", time);
    setSelectedTime(time);
    updateFormData({ horaSeleccionada: time });
  };

  const handleTabChange = (tab) => {
    // Cambio de pestaña entre horarios de mañana y tarde
    // console.log(`Pestaña ${tab} seleccionada`);
    setActiveTab(tab);
    setSelectedTime(null);
    updateFormData({ horaSeleccionada: null }); // Resetear hora seleccionada

    const pais = formData.pais || "cl"; // Obtener país seleccionado
    setSelectedHorarios(horariosPorPais[pais]?.[tab] || 4); // Asignar el ID correcto
  };

  const formatFechaHora = (fecha, hora) => {
    const fechaFormateada = new Intl.DateTimeFormat("es-CL", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    }).format(fecha).replace(/-/g, "/");
  
    return `${fechaFormateada} ${hora}`;
  };

  const handleContinue = () => {
    // Confirmación de fecha y hora seleccionadas antes de continuar
    // console.log("Fecha y hora seleccionadas al continuar:", {
      // fechaSeleccionada: formatFecha(selectedDate),
      // horaSeleccionada: selectedTime,
    // });
    updateFormData({
      // fechaSeleccionada: formatFecha(selectedDate),
      // horaSeleccionada: selectedTime,
      fechaSeleccionada: formatFechaHora(selectedDate, selectedTime),
    });
    onCalendarioSeleccionado();
  };

  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 2);

  return (
    <div
    className={`col-md-6 pads0-768 ${formData?.cadena === "eco" ? "eco-theme" : ""} ${formData?.cadena === "gmo" ? "gmo-theme" : ""}`}
    id="booking-calendar">
      <div className="row booking-calendar-container" id="react-booking">
        <div className="col-12 pads0">
          <div className="row mb20">
            <div className="col-12 text-center">
              <h3>Seleccione una fecha</h3>
            </div>
          </div>
        </div>
        <DatePicker
  selected={selectedDate}
  onChange={handleDateChange}
  className="calendar-step3"
  inline
  locale="es-custom"
  minDate={new Date(new Date().setDate(new Date().getDate()))} // Bloquea desde mañana
  maxDate={maxDate}
/>

        <div className="row text-center mb20 mt40">
          <div className="col-12">
            <h4 className="monts-sb mb0">Seleccione una cita disponible</h4>
          </div>
        </div>

        {/* Tabs adaptados al diseño */}
        <div className="row text-center mb20 select-hour justify-content-center">
          <div
            className={`col-md-5 col-6 pads0-768 select-hour-option hour-option-day ${
              activeTab === "AM" ? "hour-selected" : ""
            }`}
            onClick={() => handleTabChange("AM")}
          >
            <p className="dayhour-schedule mb0">MAÑANA</p>
            <p className="hour-schedule mb0-768">09:00 am - 14:00 pm</p>
          </div>
          <div
            className={`col-md-5 col-6 pads0-768 select-hour-option hour-option-afternoon ${
              activeTab === "PM" ? "hour-selected" : ""
            }`}
            onClick={() => handleTabChange("PM")}
          >
            <p className="dayhour-schedule mb0">TARDE</p>
            <p className="hour-schedule mb0-768">14:00 pm - 20:00 pm</p>
          </div>
        </div>

        {/* Horarios disponibles */}
        {loading ? (
          <div className="row text-center">
            <div className="col-12">
              <p>Cargando horarios disponibles...</p>
            </div>
          </div>
        ) : (
          <div className="row select-prof">
            {availableTimes.map((time) => (
              <div
                key={time}
                className="col-md-3 col-6 pads5"
                onClick={() => handleTimeSelect(time)}
              >
                <div
                  className={`available-hour mb20 ${
                    activeTab === "AM" ? "available-day" : "available-afternoon"
                  } ${selectedTime === time ? 
                    "prof-selected": ""}`
                  }
                >
                  <h3 className="monts-b mtb0">{time}</h3>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Botón Continuar */}
        {selectedDate && selectedTime && !loading && (
          <div className="row mt20 container-button-contact">
            <div
              className="step-contact-button upper text-end"
              onClick={handleContinue}
            >
              Continuar
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarComponent;
